import { Component, OnInit } from '@angular/core';
import { normalizeArray } from '../../../../functions/normalize-array';
import { splitCsv } from '../../../../functions/split-csv';
import {
  BenefitPlanDashboardResponse,
  BenefitPlanResponse,
} from '../../../../models/api/benefit-plan/benefit-plan-info-response';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { FormatPipe } from '../../../../pipes/format.pipe';
import { BaseComponent } from '../../../base/base.component';
import { IncludeLabelComponent } from '../../../core/labels/include-label.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-benefit-plan',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    TableComponent,
    ColumnComponent,
    FormatPipe,
    PageHeaderComponent,
    CardComponent,
    IncludeLabelComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-page-header label="Benefit Plan" />

    <app-loading-panel
      [state]="loading ? 'Loading' : 'Loaded'"
      loadingStyle="Box"
      [loadingHeight]="600"
      [error]="error"
    >
      <ng-template #loaded>
        <div class="tw-flex tw-gap-8">
          <app-detail-panel
            [dataSource]="benefitPlanResponse"
            class="tw-basis-1/3"
          >
            <app-detail-item
              label="Benefit plan ID"
              name="benefitPlanID"
              labelCase="none"
            />
            <app-detail-item
              label="Inception Date"
              name="inceptionDate"
              format="d"
            />
          </app-detail-panel>
          <app-detail-panel
            [dataSource]="benefitPlanResponse"
            class="tw-basis-1/3"
          >
            <app-detail-item label="Description" name="description" />
            <app-detail-item
              label="Expiration Date"
              name="expirationDate"
              format="d"
            />
          </app-detail-panel>
          <app-detail-panel
            [dataSource]="benefitPlanResponse"
            class="tw-basis-1/3"
          >
            <app-detail-item label="" name="" />
            <app-detail-item label="Benefit Frequency" name="benefitFrequency">
              <ng-template #value let-data>
                <div>{{ getFrequencyDescription(data.benefitFrequency) }}</div>
              </ng-template>
            </app-detail-item>
          </app-detail-panel>
        </div>

        <div class="tw-flex tw-gap-8 tw-py-8">
          @if (this.benefitScheduleAuth) {
            <div class="tw-basis-2/3">
              <app-card label="benefit schedule">
                <app-table
                  [dataSource]="benefitSchedule"
                  [pageSize]="5"
                  recordLabel="Benefit schedule"
                  [showFilter]="true"
                  [showFilterButton]="true"
                  [globalFilterFields]="[
                    'fromDate',
                    'throughDate',
                    'frequency',
                    'rate',
                  ]"
                  filterPlaceholder="Search benefit schedule"
                >
                  <app-column
                    name="fromDate"
                    label="From Date"
                    width="25%"
                    format="d"
                  />
                  <app-column
                    name="throughDate"
                    label="To Date"
                    width="25%"
                    format="d"
                  />
                  <app-column name="frequency" label="Frequency" width="25%">
                    <ng-template let-data>
                      <div>{{ getFrequencyDescription(data.frequency) }}</div>
                    </ng-template>
                  </app-column>
                  <app-column name="rate" label="Rate" width="25%" format="c" />
                </app-table>
              </app-card>
            </div>
          }
          @if (this.benefitSummaryAuth) {
            <div class="tw-basis-1/3">
              <app-card label="period">
                <app-detail-panel [dataSource]="benefitPlanResponse">
                  <app-detail-item
                    label="Elimination Period"
                    name="eliminationPeriod"
                  />
                  <app-detail-item label="Appeal Period" name="appealPeriod" />
                  <app-detail-item label="Max Appeal" name="maxAppeal" />
                  <app-detail-item
                    label="Relapse Period"
                    name="relapsePeriod"
                  />
                </app-detail-panel>
              </app-card>
            </div>
          }
        </div>
        @if (this.benefitSummaryAuth) {
          <div class="tw-flex tw-gap-8 tw-py-8">
            <div class="tw-basis-2/3">
              <app-card label="calculation">
                <div class="tw-flex tw-gap-8">
                  <app-detail-panel
                    [dataSource]="benefitPlanResponse"
                    class="tw-basis-1/2"
                  >
                    <app-detail-item
                      label="Max Benefit Amount"
                      name="maxBenefitAmount"
                      format="c"
                    />
                    <app-detail-item
                      label="Max Benefit Duration"
                      name="maxBenefitDuration"
                    />
                    <app-detail-item
                      label="Min Benefit Amount"
                      name="minBenefitAmount"
                      format="c"
                    />
                    <app-detail-item label="Include">
                      <ng-template #value>
                        <app-include-label
                          [include]="included"
                          [exclude]="excluded"
                        />
                      </ng-template>
                    </app-detail-item>
                  </app-detail-panel>
                  <app-detail-panel
                    [dataSource]="benefitPlanResponse"
                    class="tw-basis-1/2"
                  >
                    <app-detail-item
                      label="Freeze Wages"
                      name="freezeWages"
                      format="b"
                    />
                    <app-detail-item
                      label="State Award"
                      name="stateAward"
                      format="b"
                    />
                    <app-detail-item
                      label="Partial Benefit"
                      name="partialBenefit"
                      format="b"
                    />
                    <app-detail-item
                      label="Alternate Benefit"
                      name="alternateAmt"
                      format="b"
                    />
                  </app-detail-panel>
                </div>
              </app-card>
            </div>
            <div class="tw-basis-1/3">
              <app-card label="eligibility">
                <ul class="tw-list-disc tw-list-inside tw-p-2">
                  <li>{{ benefitPlanResponse?.eligibilityClass }}</li>
                  <li>{{ benefitPlanResponse?.fullTimeEligibility }}</li>
                  <li>{{ benefitPlanResponse?.partTimeEligibility }}</li>
                </ul>
              </app-card>
            </div>
          </div>

          <div class="tw-pt-8">
            <app-card label="schedule">
              <app-table
                [dataSource]="benefitPlanDetails"
                [pageSize]="5"
                recordLabel="Schedule"
                [showFilter]="true"
                [showFilterButton]="true"
                [globalFilterFields]="[
                  'ncs',
                  'frequency',
                  'time',
                  'percent',
                  'max',
                  'ficaTaxable',
                  'ficaNonTaxable',
                  'noFicaTaxable',
                  'noFicaNonTaxable',
                ]"
                filterPlaceholder="Search schedule"
              >
                <ng-template #columnGroup>
                  <!-- TODO: add column group component -->
                  <!-- <tr>
                  <th colspan="5"></th>
                  <th colspan="4">Earnings Code</th>
                </tr> -->
                </ng-template>
                <app-column name="ncs" label="NCS From-Thru" />
                <app-column name="frequency" label="Scheduled Frequency">
                  <ng-template let-data>
                    <div>{{ getFrequencyDescription(data.frequency) }}</div>
                  </ng-template>
                </app-column>
                <app-column name="time1" label="Time">
                  <ng-template let-data>
                    <div class="tw-flex tw-flex-col row">
                      <div>{{ data.time[0] }}</div>
                      <div>{{ data.time[1] }}</div>
                      <div>{{ data.time[2] }}</div>
                      <div>{{ data.time[3] }}</div>
                    </div>
                  </ng-template>
                </app-column>
                <app-column name="rate1" label="Percent/Rate">
                  <ng-template let-data>
                    <div class="tw-flex tw-flex-col  row">
                      <div>{{ data.percent[0] }}</div>
                      <div>{{ data.percent[1] }}</div>
                      <div>{{ data.percent[2] }}</div>
                      <div>{{ data.percent[3] }}</div>
                    </div>
                  </ng-template>
                </app-column>
                <app-column name="maxPercent1" label="Max%">
                  <ng-template let-data>
                    <div class="tw-flex tw-flex-col  row">
                      <div>{{ data.max[0] | format: '%' }}</div>
                      <div>{{ data.max[1] | format: '%' }}</div>
                      <div>{{ data.max[2] | format: '%' }}</div>
                      <div>{{ data.max[3] | format: '%' }}</div>
                    </div>
                  </ng-template>
                </app-column>

                <app-column name="ficaTax1" label="FICA/Taxable">
                  <ng-template let-data>
                    <div class="tw-flex tw-flex-col row">
                      <div>{{ data.ficaTaxable[0] }}</div>
                      <div>{{ data.ficaTaxable[1] }}</div>
                      <div>{{ data.ficaTaxable[2] }}</div>
                      <div>{{ data.ficaTaxable[3] }}</div>
                    </div>
                  </ng-template>
                </app-column>
                <app-column name="ficaNonTax1" label="FICA/Non Tax">
                  <ng-template let-data>
                    <div class="tw-flex tw-flex-col row">
                      <div>{{ data.ficaNonTaxable[0] }}</div>
                      <div>{{ data.ficaNonTaxable[1] }}</div>
                      <div>{{ data.ficaNonTaxable[2] }}</div>
                      <div>{{ data.ficaNonTaxable[3] }}</div>
                    </div>
                  </ng-template>
                </app-column>

                <app-column name="noFicaTax1" label="No FICA/Taxable">
                  <ng-template let-data>
                    <div class="tw-flex tw-flex-col row">
                      <div>{{ data.noFicaTaxable[0] }}</div>
                      <div>{{ data.noFicaTaxable[1] }}</div>
                      <div>{{ data.noFicaTaxable[2] }}</div>
                      <div>{{ data.noFicaTaxable[3] }}</div>
                    </div>
                  </ng-template>
                </app-column>
                <app-column name="noFicaNonTax1" label="No FICA/Non Tax">
                  <ng-template let-data>
                    <div class="tw-flex tw-flex-col  row">
                      <div>{{ data.noFicaNonTaxable[0] }}</div>
                      <div>{{ data.noFicaNonTaxable[1] }}</div>
                      <div>{{ data.noFicaNonTaxable[2] }}</div>
                      <div>{{ data.noFicaNonTaxable[3] }}</div>
                    </div>
                  </ng-template>
                </app-column>
              </app-table>
            </app-card>
          </div>
        }
      </ng-template>
    </app-loading-panel>
  `,
  styles: `
    .row div {
      height: 20px;
    }
  `,
})
export class BenefitPlanComponent extends BaseComponent implements OnInit {
  benefitPlanInfo: BenefitPlanDashboardResponse;
  benefitPlanResponse: BenefitPlanResponse;
  benefitPlanDetails: any[] = [];
  benefitSchedule: any[] = [];
  include: { included: boolean; description: string }[] = [];
  benefitSummaryAuth = false;
  benefitScheduleAuth = false;

  claim: ClaimRow;

  included: string[] = [];

  excluded: string[] = [];

  loading = true;
  error: any = null;

  @HandleLoadingState()
  async ngOnInit() {
    this.view.hideLoading();
    this.benefitSummaryAuth = this.session.authorize('BenefitSummary');
    this.benefitScheduleAuth = this.session.authorize('BenefitSchedule');
    this.claim = this.view.currentClaim();

    this.benefitPlanInfo = await this.api.benefitPlan.get(
      this.claim.recordSource,
      this.claim.recordId,
      this.claim.contractNumber,
    );

    this.processBenefitPlanData();
    this.populateIncludeItems();
  }

  private processBenefitPlanData(): void {
    if (!this.benefitPlanInfo) return;

    this.benefitPlanResponse = this.benefitPlanInfo.benefitPlanResponse;
    this.benefitPlanDetails = normalizeArray(
      this.benefitPlanInfo.listBenefitPlanDetails,
    );
    this.benefitSchedule = this.benefitPlanInfo.benefitSchedules || [];
  }

  private populateIncludeItems(): void {
    if (!this.benefitPlanResponse) {
      this.include = [];
      return;
    }

    this.included = splitCsv(this.benefitPlanResponse.include);
    this.excluded = splitCsv(this.benefitPlanResponse.doNotInclude);
  }

  getFrequencyDescription(frequency: string): string {
    switch (frequency) {
      case 'DA':
        return 'Daily';
      case 'MO':
        return 'Monthly';
      case 'AN':
        return 'Annually';
      case 'HO':
        return 'Hourly';
      case 'QU':
        return 'Quarterly';
      case 'WE':
        return 'Weekly';
      case 'SM':
        return 'Semi-Monthly';
      case 'BI':
        return 'Bi-Weekly';
      default:
        return frequency;
    }
  }
}
